import { useState } from 'react';

interface WaterGaugeData {
  id: string;
  shipName: string;
  draftFore: number;
  draftMid: number;
  draftAft: number;
  calculatedWeight: number;
  measureTime: string;
  operator: string;
}

export default function WaterGauge() {
  const [gaugeData] = useState<WaterGaugeData[]>([
    { id: 'WG001', shipName: '远洋货轮-001', draftFore: 8.5, draftMid: 9.2, draftAft: 9.8, calculatedWeight: 45600, measureTime: '2025-01-15 09:30', operator: '无人机-03' },
    { id: 'WG002', shipName: '集装箱船-A23', draftFore: 10.2, draftMid: 10.8, draftAft: 11.5, calculatedWeight: 62800, measureTime: '2025-01-15 10:45', operator: '无人机-05' },
    { id: 'WG003', shipName: '散货船-B56', draftFore: 7.8, draftMid: 8.4, draftAft: 9.0, calculatedWeight: 38200, measureTime: '2025-01-15 11:50', operator: '无人机-03' },
    { id: 'WG004', shipName: '油轮-C89', draftFore: 12.5, draftMid: 13.2, draftAft: 13.8, calculatedWeight: 78500, measureTime: '2025-01-15 08:15', operator: '无人机-07' },
  ]);

  const avgDraft = gaugeData.reduce((sum, item) => sum + (item.draftFore + item.draftMid + item.draftAft) / 3, 0) / gaugeData.length;
  const totalWeight = gaugeData.reduce((sum, item) => sum + item.calculatedWeight, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">水尺计重系统</h2>
        <button className="px-4 py-2 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-add-line mr-2"></i>新增测量
        </button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">今日测量</span>
            <i className="ri-ruler-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">{gaugeData.length}</div>
          <div className="text-xs text-slate-500 mt-1">艘次</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">平均吃水</span>
            <i className="ri-water-flash-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">{avgDraft.toFixed(1)}</div>
          <div className="text-xs text-slate-500 mt-1">米</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">总货重</span>
            <i className="ri-scales-3-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">{(totalWeight / 1000).toFixed(1)}K</div>
          <div className="text-xs text-slate-500 mt-1">吨</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">效率提升</span>
            <i className="ri-speed-up-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-teal-400">85%</div>
          <div className="text-xs text-slate-500 mt-1">vs 人工</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">水尺观测画面</h3>
          <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative">
            <img 
              src="https://public.readdy.ai/ai/img_res/11d52c00f92afc9fba4f6e5a5bc7b0ac.jpg"
              alt="水尺观测"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute top-4 left-4 bg-teal-500 text-white px-3 py-1 rounded-full text-xs font-medium">
              前吃水: 8.5m
            </div>
            <div className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-xs">
              无人机-03
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">吃水深度分析</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">前吃水</span>
                <span className="text-sm font-medium text-white">8.5m</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div className="bg-teal-500 h-2 rounded-full" style={{ width: '61%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">中吃水</span>
                <span className="text-sm font-medium text-white">9.2m</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div className="bg-teal-500 h-2 rounded-full" style={{ width: '66%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">后吃水</span>
                <span className="text-sm font-medium text-white">9.8m</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div className="bg-teal-500 h-2 rounded-full" style={{ width: '70%' }}></div>
              </div>
            </div>
            <div className="pt-4 border-t border-slate-700">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">计算货重</span>
                <span className="text-xl font-bold text-teal-400">45,600 吨</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">测量记录</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">记录编号</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">船舶名称</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">前吃水(m)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">中吃水(m)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">后吃水(m)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">计算货重(吨)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">测量时间</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">操作员</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {gaugeData.map((item) => (
                <tr key={item.id} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{item.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{item.shipName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{item.draftFore}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{item.draftMid}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{item.draftAft}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{item.calculatedWeight.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{item.measureTime}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{item.operator}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
